// Modal-local Task composer state with a narrow bridge to bounded page-memory retention. Notes, queue
// observations, and stream state stay in the modal stash; only draft text and exact request correlation
// are synchronised from the parent registry.

import { useCallback, useEffect, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { QueuedRunObservation } from './task-thread-queue.ts';
import type {
  PendingTaskRequest,
  TaskComposerRetention,
  TaskComposerRetentionEvent,
} from './task-request-identity.ts';

export interface ComposerStash {
  draft: string;
  note: string | null;
  queuedRun: QueuedRunObservation | null;
  /** Retained only while the transport outcome is unknown; an exact retry reuses this identity. */
  pendingRequest: PendingTaskRequest | null;
}

export interface RetainedComposerState {
  readonly draft: string;
  readonly setDraft: (value: string) => void;
  readonly note: string | null;
  readonly setNote: (value: string | null) => void;
}

/** Write local state through the reload stash and react to parent settlement after an earlier unmount. */
export function useRetainedTaskComposerState(
  stash: MutableRefObject<ComposerStash>,
  retention: TaskComposerRetention | null,
  onRetentionEvent: (event: TaskComposerRetentionEvent) => boolean,
): RetainedComposerState {
  const [draft, setDraftState] = useState(() => stash.current.draft);
  const [note, setNoteState] = useState<string | null>(() => stash.current.note);
  const setDraft = useCallback(
    (value: string) => {
      stash.current.draft = value;
      onRetentionEvent({ kind: 'draft_changed', draft: value });
      setDraftState(value);
    },
    [onRetentionEvent, stash],
  );
  const setNote = useCallback(
    (value: string | null) => {
      stash.current.note = value;
      setNoteState(value);
    },
    [stash],
  );
  useEffect(() => {
    const retainedDraft = retention?.draft ?? '';
    stash.current.pendingRequest = retention?.pendingRequest ?? null;
    if (stash.current.draft === retainedDraft) return;
    stash.current.draft = retainedDraft;
    setDraftState(retainedDraft);
  }, [retention, stash]);
  return { draft, setDraft, note, setNote };
}
